/**
 * FunctionLocationTracker
 * -----------------------------------------------------------------------------
 * Tracks where your custom Excel functions live in a workbook and keeps those
 * locations consistent as users edit, switch sheets, or rename worksheets.
 *
 * ## What it does
 * - Records a mapping from a **function invocation key** (e.g. a cache key like
 *   `"share_price_data:TSLA|USD"`) to the **cell(s)** that contain that formula.
 * - Provides fast lookups:
 *   - all locations for a given function key
 *   - locations for a key on a specific sheet bucket (sheetId preferred)
 *   - reverse lookup: given a sheet+address, find the function key
 * - Resolves and caches **worksheet IDs** for stable indexing (sheet renames
 *   won’t break lookups) and migrates indexes from sheetName → sheetId once known.
 * - Subscribes to Excel’s **worksheet rename** events and updates all mappings,
 *   indexes, and caches in place.
 * - Periodically **cleans up** stale entries (cells that no longer contain the
 *   function) by reading formulas from the workbook.
 *
 * ## Data model
 * - Master map (authoritative): `"Sheet!A1" -> LocationMapping`
 * - Secondary indexes:
 *   - `byKey: key -> Set<"Sheet!A1">`
 *   - `byKeyBySheet: key -> (sheetKey -> Set<"Sheet!A1">)`
 *     where `sheetKey` is the **sheetId** when known, otherwise **sheet name** as a
 *     temporary bucket that later migrates to the id bucket when resolved.
 *
 * ### LocationMapping
 * ```ts
 * interface LocationMapping {
 *   key: string;          // function key (e.g. "fnName:arg1|arg2")
 *   functionName: string; // "fnName" extracted from key (before ':')
 *   sheet: string;        // worksheet name
 *   address: string;      // cell address within the sheet ("A1", "$B$2", etc.)
 *   formula: string;      // the formula string currently in the cell
 *   sheetId?: string|null;// Excel worksheet id, when known
 * }
 * ```
 *
 * ## Lifecycle & usage
 * - Singleton: call `FunctionLocationTracker.getInstance()` anywhere.
 * - Optional hard guarantee: call `await tracker.init()` once at startup to attach
 *   the worksheet rename handler eagerly (otherwise it’s lazily available once Excel
 *   is ready, but `init()` gives you determinism).
 * - Register or update a location whenever you write a formula:
 *   ```ts
 *   const tracker = FunctionLocationTracker.getInstance();
 *   tracker.registerCell(functionKey, "'My Sheet'!$A$1", formulaText);
 *   ```
 * - Lookups:
 *   ```ts
 *   tracker.getLocationsForKey(key);                // O(K) for that key
 *   tracker.getLocationsForKeyOnSheet(key, sheetId);// prefer sheetId when known
 *   tracker.getFunctionKeyAt("My Sheet", "A1");     // reverse lookup
 *   ```
 * - Maintenance:
 *   ```ts
 *   await tracker.cleanup(); // removes mappings for cells that no longer contain the function
 *   tracker.clear();         // dev/teardown
 *   ```
 *
 * ## Caching & debouncing
 * - Sheet IDs are cached per sheet name for `CACHE_TTL` (default 5 minutes).
 * - Pending name→id resolutions are batched and **debounced** (base 250ms, capped at
 *   2s) to limit round-trips to the Excel runtime.
 * - When an id is learned, mappings & indexes for that sheet are migrated from
 *   the name bucket to the id bucket.
 *
 * ## Events
 * - Subscribes to `worksheets.onNameChanged`. On rename:
 *   - Updates the `sheetIdCache` (oldName → newName)
 *   - Re-keys all affected master entries (`"Old!A1"` → `"New!A1"`)
 *   - Moves index entries to the new `sheetId` bucket
 *   - Logs a summary via `log.info`
 *
 * ## Address parsing
 * - Accepts addresses like: `'My Sheet'!$A$1` or `My Sheet!A1`
 * - `parseAddress()` returns `{ sheetName, cellAddress }` or `null` if invalid.
 *   (If you need stricter parsing—e.g., handling 3D refs—extend the regex.)
 *
 * ## Cleanup details
 * - `cleanup()` walks all tracked ranges, reads `formulas`, and removes entries
 *   whose cells no longer contain the recorded `functionName` (case-insensitive
 *   substring check). It also prunes mappings to missing sheets/ranges.
 *
 * ## Complexity (hot-path)
 * - `registerCell`: O(1) amortized for map & index maintenance (plus any pending
 *   debounce bookkeeping)
 * - `getLocationsForKey`: O(K) over that key’s locations
 * - `getLocationsForKeyOnSheet`: O(K_s) for that key on a specific sheet bucket
 *
 * ## Logging
 * - Uses `log.debug/info/warn/error` (imported from `../utils/logger`) for traceability.
 *   These calls are optional/no-ops depending on your logger implementation.
 *
 * ## Notes and constraints
 * - Environment: Office.js + Excel JavaScript API (uses `Excel.run`, `Office.onReady`).
 * - Strings used as **sheet buckets** are transitional; prefer `sheetId` when available.
 * - This class is synchronous on the hot path; Excel I/O is confined to:
 *   - `init()`, `getSheetId() / collectPendingSheetIds()`, `cleanup()`, and the rename handler.
 * - Not thread-safe (JavaScript single-threaded). Avoid concurrent manual mutation
 *   of the tracker from multiple isolated runtimes for the same workbook.
 *
 * ## Example
 * ```ts
 * const tracker = FunctionLocationTracker.getInstance();
 * await tracker.init(); // optional but recommended at startup
 *
 * // When you write a custom function formula:
 * const key = "share_price_data:AAPL|USD"; // e.g., produced by your KeyBuilder
 * const addr = "'Prices'!B2";
 * const formula = "=MYFN.SHARE_PRICE_DATA(\"AAPL\",\"USD\")";
 * tracker.registerCell(key, addr, formula);
 *
 * // Later, find every cell for that request:
 * const all = tracker.getLocationsForKey(key);
 *
 * // Or only on a given sheet bucket (prefer an id if you have it):
 * const sid = await tracker.getSheetId("Prices");
 * const scoped = sid ? tracker.getLocationsForKeyOnSheet(key, sid)
 *                    : tracker.getLocationsForKeyOnSheet(key, "Prices");
 *
 * // Periodic hygiene:
 * const removed = await tracker.cleanup();
 * ```
 */
import { log } from '../utils/logger';
export class FunctionLocationTracker {
    // ── Singleton (sync) ────────────────────────────────────────────────────────
    static instance = null;
    static getInstance() {
        return this.instance ?? (this.instance = new FunctionLocationTracker());
    }
    constructor() { }
    // ── Init (attach handlers once if you want a strong guarantee) ──────────────
    inited = false;
    initPromise = null;
    /** Call once at startup if you want a hard guarantee the rename handler is attached. */
    async init() {
        if (this.inited)
            return;
        if (this.initPromise)
            return this.initPromise;
        this.initPromise = (async () => {
            await Office.onReady();
            await Excel.run(async (ctx) => {
                ctx.workbook.worksheets.onNameChanged.add(this.handleSheetNameChange);
                await ctx.sync();
            });
            this.inited = true;
        })();
        return this.initPromise;
    }
    // ── Storage ─────────────────────────────────────────────────────────────────
    // Master map: id "Sheet!A1" -> mapping
    map = new Map();
    // Index: key -> set of ids
    byKey = new Map();
    // Index: key -> (sheetKey -> set of ids)
    // sheetKey is sheetId when known, else sheetName (temporary)
    byKeyBySheet = new Map();
    // Index: cellId -> set of mapping ids
    byCell = new Map();
    // Sheet id cache and pending names to resolve
    sheetIdCache = new Map();
    pendingSheetNames = new Set();
    CACHE_TTL = 5 * 60 * 1000; // 5 min
    DEBOUNCE_DELAY = 250; // base
    MAX_DEBOUNCE_DELAY = 2000; // cap
    debounceTimer = null;
    // ── Public API (fast) ───────────────────────────────────────────────────────
    /** Fast path: all locations for this function key. O(K) in that key. */
    getLocationsForKey(key) {
        const ids = this.byKey.get(key);
        if (!ids || ids.size === 0)
            return [];
        const out = [];
        for (const id of ids) {
            const m = this.map.get(id);
            if (m)
                out.push(m);
        }
        return out;
    }
    /** Fast path: only locations for this key on a given sheet bucket (sheetId preferred). */
    getLocationsForKeyOnSheet(key, sheetKey) {
        const inner = this.byKeyBySheet.get(key);
        if (!inner)
            return [];
        const ids = inner.get(sheetKey);
        if (!ids || ids.size === 0)
            return [];
        const out = [];
        for (const id of ids) {
            const m = this.map.get(id);
            if (m)
                out.push(m);
        }
        return out;
    }
    /** Full list (debug/introspection; avoid on hot path). */
    getAllMappings() {
        return Array.from(this.map.values());
    }
    /** Reverse lookup at a specific cell. */
    getFunctionKeysAt(sheet, address) {
        return this.getLocationsForCell(sheet, address).map((mapping) => mapping.key);
    }
    getFunctionKeyAt(sheet, address) {
        const mappings = this.getLocationsForCell(sheet, address);
        if (mappings.length !== 1) {
            return null;
        }
        return mappings[0].key;
    }
    removeMappingsForCell(sheet, address) {
        const cellId = this.buildCellId(sheet, address);
        const ids = Array.from(this.byCell.get(cellId) ?? []);
        for (const id of ids) {
            const mapping = this.map.get(id);
            if (!mapping)
                continue;
            this.removeMapping(id, mapping.key);
        }
    }
    removeMappingsForCells(cells) {
        for (const cell of cells) {
            this.removeMappingsForCell(cell.sheet, cell.address);
        }
    }
    /** Parse "'My Sheet'!$A$1" or "My Sheet!A1". */
    static parseAddress(address) {
        const m = address.match(/^'?(.*?)'?!([A-Za-z0-9$]+)$/);
        if (!m)
            return null;
        return { sheetName: m[1], cellAddress: m[2] };
        // Note: if you need stricter parsing (e.g., colon names), enhance the regex.
    }
    /** Resolve sheetId for a name; caches; touches Excel if needed. */
    async getSheetId(sheetName) {
        const hit = this.sheetIdCache.get(sheetName);
        if (hit && Date.now() - hit.lastUpdated < this.CACHE_TTL)
            return hit.id;
        this.pendingSheetNames.add(sheetName);
        await this.collectPendingSheetIds();
        return this.sheetIdCache.get(sheetName)?.id ?? null;
    }
    /** Register/update a cell location for a function key. */
    registerCell(functionKey, address, formula) {
        const parsed = FunctionLocationTracker.parseAddress(address);
        if (!parsed)
            throw new Error(`Invalid address: ${address}`);
        const { sheetName, cellAddress: rawCellAddress } = parsed;
        const cellAddress = this.normalizeCellAddress(rawCellAddress);
        const cellId = this.buildCellId(sheetName, cellAddress);
        this.removeMappingsForCellWithDifferentFormula(cellId, formula);
        const id = this.buildMappingId(cellId, functionKey);
        // Cheap functionName extraction (before ':')
        const sep = functionKey.indexOf(':');
        const functionName = sep === -1 ? functionKey : functionKey.slice(0, sep);
        const prev = this.map.get(id);
        if (prev && prev.key === functionKey && prev.formula === formula && prev.sheet === sheetName) {
            // nothing changed
            return;
        }
        // Queue sheet-id resolution if stale
        if (!this.isCacheValid(sheetName)) {
            this.pendingSheetNames.add(sheetName);
        }
        // Compose mapping; reuse any known id
        const cached = this.sheetIdCache.get(sheetName);
        const now = Date.now();
        const next = {
            key: functionKey,
            functionName,
            sheet: sheetName,
            address: cellAddress,
            formula,
            sheetId: cached?.id ?? prev?.sheetId ?? null,
            cellId,
            mappingId: id,
            registeredAt: now,
        };
        // Write master
        this.map.set(id, next);
        this.indexAddCell(cellId, id);
        // Maintain indexes
        if (!prev) {
            // brand new id
            this.indexAdd(functionKey, next.sheetId ?? sheetName, id);
        }
        else if (prev.key !== functionKey) {
            // key changed
            this.indexRemove(prev.key, prev.sheetId ?? prev.sheet, id);
            this.indexAdd(functionKey, next.sheetId ?? sheetName, id);
        }
        else {
            // same key; maybe sheetId changed name->id
            const oldSheetKey = prev.sheetId ?? prev.sheet;
            const newSheetKey = next.sheetId ?? next.sheet;
            if (oldSheetKey !== newSheetKey) {
                this.indexSwapSheetKey(functionKey, oldSheetKey, newSheetKey, id);
            }
        }
        // Debounced sheet-id resolution
        this.scheduleCollect();
    }
    /** Remove everything (dev/cleanup). */
    clear() {
        this.map.clear();
        this.byKey.clear();
        this.byKeyBySheet.clear();
        this.byCell.clear();
        this.sheetIdCache.clear();
        this.pendingSheetNames.clear();
        if (this.debounceTimer) {
            clearTimeout(this.debounceTimer);
            this.debounceTimer = null;
        }
    }
    /** Remove stale entries (cells no longer contain our function). Returns removed ids. */
    async cleanup() {
        const removed = [];
        const lookups = [];
        await Excel.run(async (ctx) => {
            // Build lookups
            for (const [id, m] of this.map) {
                try {
                    const ws = m.sheetId ? ctx.workbook.worksheets.getItem(m.sheetId) : ctx.workbook.worksheets.getItem(m.sheet);
                    const range = ws.getRange(m.address);
                    range.load('formulas');
                    lookups.push({ id, key: m.key, functionName: m.functionName, range });
                }
                catch {
                    // worksheet or range not found
                    this.removeMapping(id, m.key);
                    removed.push(id);
                }
            }
            await ctx.sync();
            // Verify formulas
            for (const l of lookups) {
                let f;
                try {
                    f = l.range.formulas?.[0]?.[0];
                }
                catch {
                    this.removeMapping(l.id, l.key);
                    removed.push(l.id);
                    continue;
                }
                if (!f || !this.functionNameAppearsInFormula(f, l.functionName)) {
                    this.removeMapping(l.id, l.key);
                    removed.push(l.id);
                }
            }
        });
        return removed;
    }
    // ── Internal: indexing helpers ──────────────────────────────────────────────
    indexAdd(key, sheetKey, id) {
        // byKey
        let set = this.byKey.get(key);
        if (!set)
            this.byKey.set(key, (set = new Set()));
        set.add(id);
        // byKeyBySheet
        let inner = this.byKeyBySheet.get(key);
        if (!inner)
            this.byKeyBySheet.set(key, (inner = new Map()));
        let bucket = inner.get(sheetKey);
        if (!bucket)
            inner.set(sheetKey, (bucket = new Set()));
        bucket.add(id);
    }
    indexRemove(key, sheetKey, id) {
        // byKey
        const set = this.byKey.get(key);
        if (set) {
            set.delete(id);
            if (set.size === 0)
                this.byKey.delete(key);
        }
        // byKeyBySheet
        const inner = this.byKeyBySheet.get(key);
        if (inner) {
            const bucket = inner.get(sheetKey);
            if (bucket) {
                bucket.delete(id);
                if (bucket.size === 0)
                    inner.delete(sheetKey);
            }
            if (inner.size === 0)
                this.byKeyBySheet.delete(key);
        }
    }
    indexSwapSheetKey(key, oldSheetKey, newSheetKey, id) {
        if (oldSheetKey === newSheetKey)
            return;
        const inner = this.byKeyBySheet.get(key);
        if (inner) {
            const oldB = inner.get(oldSheetKey);
            if (oldB) {
                oldB.delete(id);
                if (oldB.size === 0)
                    inner.delete(oldSheetKey);
            }
            let newB = inner.get(newSheetKey);
            if (!newB)
                inner.set(newSheetKey, (newB = new Set()));
            newB.add(id);
            if (inner.size === 0)
                this.byKeyBySheet.delete(key);
        }
    }
    removeMapping(id, key) {
        const m = this.map.get(id);
        if (!m)
            return;
        this.map.delete(id);
        const sheetKey = m.sheetId ?? m.sheet;
        this.indexRemove(key, sheetKey, id);
        this.indexRemoveCell(m.cellId, id);
    }
    removeMappingsByIds(mappingIds) {
        for (const id of mappingIds) {
            const m = this.map.get(id);
            if (!m)
                continue;
            this.removeMapping(id, m.key);
        }
    }
    isCacheValid(sheetName) {
        const hit = this.sheetIdCache.get(sheetName);
        return !!hit && Date.now() - hit.lastUpdated < this.CACHE_TTL;
    }
    scheduleCollect() {
        if (this.debounceTimer)
            clearTimeout(this.debounceTimer);
        const delay = Math.min(this.DEBOUNCE_DELAY * (1 + this.pendingSheetNames.size / 10), this.MAX_DEBOUNCE_DELAY);
        this.debounceTimer = setTimeout(() => {
            this.collectPendingSheetIds().catch(() => { });
        }, delay);
    }
    /** Resolve pending sheet names → ids; migrate index buckets name->id where needed. */
    async collectPendingSheetIds() {
        if (this.pendingSheetNames.size === 0)
            return;
        const names = Array.from(this.pendingSheetNames);
        this.pendingSheetNames.clear();
        await Excel.run(async (ctx) => {
            try {
                const sheets = ctx.workbook.worksheets;
                sheets.load('items/name,items/id');
                await ctx.sync();
                const now = Date.now();
                const nameToId = new Map();
                for (const ws of sheets.items) {
                    nameToId.set(ws.name, ws.id);
                }
                for (const name of names) {
                    const id = nameToId.get(name);
                    if (!id)
                        continue;
                    this.sheetIdCache.set(name, { id, lastUpdated: now });
                    // Migrate mappings on this sheet from name-bucket to id-bucket if needed
                    for (const [mapId, m] of this.map) {
                        if (m.sheet !== name)
                            continue;
                        if (m.sheetId === id)
                            continue;
                        const oldSheetKey = m.sheetId ?? m.sheet;
                        m.sheetId = id; // update in place
                        this.indexSwapSheetKey(m.key, oldSheetKey, id, mapId);
                    }
                }
                log.debug?.('Tracker: collected sheet ids', { resolved: names.length });
            }
            catch (err) {
                // Requeue on failure
                for (const n of names)
                    this.pendingSheetNames.add(n);
                log.warn('Tracker: collectPendingSheetIds failed; will retry', err);
            }
        });
    }
    // ── Sheet rename handling (keeps map/byKey/byKeyBySheet consistent) ─────────
    handleSheetNameChange = async (event) => {
        await Excel.run(async (ctx) => {
            try {
                const ws = ctx.workbook.worksheets.getItem(event.worksheetId);
                ws.load(['name', 'id']);
                await ctx.sync();
                const newName = ws.name;
                const sid = ws.id;
                // Find old name via cache; update cache entry
                let oldName;
                for (const [name, data] of this.sheetIdCache.entries()) {
                    if (data.id === sid) {
                        oldName = name;
                        break;
                    }
                }
                if (!oldName) {
                    this.sheetIdCache.set(newName, { id: sid, lastUpdated: Date.now() });
                    return;
                }
                this.sheetIdCache.delete(oldName);
                this.sheetIdCache.set(newName, { id: sid, lastUpdated: Date.now() });
                // Collect affected rows first
                const moves = [];
                for (const [id, m] of this.map) {
                    if (m.sheet === oldName) {
                        const newCellId = this.buildCellId(newName, m.address);
                        const newId = this.buildMappingId(newCellId, m.key);
                        moves.push({ oldId: id, newId, newCellId, m });
                    }
                }
                // Apply moves
                for (const { oldId, newId, newCellId, m } of moves) {
                    const updated = {
                        ...m,
                        sheet: newName,
                        sheetId: sid,
                        cellId: newCellId,
                        mappingId: newId,
                    };
                    this.map.set(newId, updated);
                    this.map.delete(oldId);
                    this.indexRemoveCell(m.cellId, oldId);
                    this.indexAddCell(updated.cellId, newId);
                    // byKey: swap id within the set
                    const s = this.byKey.get(m.key);
                    if (s) {
                        s.delete(oldId);
                        s.add(newId);
                        if (s.size === 0)
                            this.byKey.delete(m.key);
                    }
                    // byKeyBySheet:
                    const inner = this.byKeyBySheet.get(m.key);
                    if (inner) {
                        const sourceKey = m.sheetId && inner.has(m.sheetId) ? m.sheetId : oldName;
                        const src = inner.get(sourceKey);
                        if (src) {
                            src.delete(oldId);
                            let dest = inner.get(sid);
                            if (!dest)
                                inner.set(sid, (dest = new Set()));
                            dest.add(newId);
                            if (src.size === 0)
                                inner.delete(sourceKey);
                        }
                        if (inner.size === 0)
                            this.byKeyBySheet.delete(m.key);
                    }
                }
                log.info('Tracker: updated mappings for renamed sheet', { oldName, newName, count: moves.length });
            }
            catch (error) {
                log.error('Tracker: error handling sheet rename', error);
            }
        });
    };
    // ── Debug helpers ───────────────────────────────────────────────────────────
    logStats() {
        log.info('Tracker stats', {
            total: this.map.size,
            keys: this.byKey.size,
        });
    }
    // Tiny hash helper (optional)
    static hashKey(key) {
        let h = 0;
        for (let i = 0; i < key.length; i++)
            h = (h * 31 + key.charCodeAt(i)) >>> 0;
        return h.toString(36);
    }
    buildCellId(sheetName, cellAddress) {
        return `${sheetName}!${this.normalizeCellAddress(cellAddress)}`;
    }
    buildMappingId(cellId, functionKey) {
        return `${cellId}::${FunctionLocationTracker.hashKey(functionKey)}`;
    }
    indexAddCell(cellId, mappingId) {
        let set = this.byCell.get(cellId);
        if (!set) {
            this.byCell.set(cellId, (set = new Set()));
        }
        set.add(mappingId);
    }
    indexRemoveCell(cellId, mappingId) {
        const set = this.byCell.get(cellId);
        if (!set)
            return;
        set.delete(mappingId);
        if (set.size === 0) {
            this.byCell.delete(cellId);
        }
    }
    getLocationsForCell(sheet, address) {
        const cellId = this.buildCellId(sheet, address);
        const ids = this.byCell.get(cellId);
        if (!ids || ids.size === 0)
            return [];
        const out = [];
        for (const id of ids) {
            const m = this.map.get(id);
            if (m)
                out.push(m);
        }
        return out;
    }
    removeMappingsForCellWithDifferentFormula(cellId, formula) {
        const ids = Array.from(this.byCell.get(cellId) ?? []);
        for (const id of ids) {
            const existing = this.map.get(id);
            if (!existing)
                continue;
            if (existing.formula !== formula) {
                this.removeMapping(id, existing.key);
            }
        }
    }
    normalizeCellAddress(address) {
        return address.replace(/\$/g, '').toUpperCase();
    }
    functionNameAppearsInFormula(formula, functionName) {
        const f = formula.toUpperCase();
        const raw = functionName.toUpperCase();
        if (f.includes(raw))
            return true;
        if (/^[A-Z0-9_]+$/.test(raw)) {
            return f.includes(`ALPHA.${raw}`);
        }
        return false;
    }
}
//# sourceMappingURL=functionLocationTracker.js.map